import { computed } from "@angular/core";
import { TrailParams, TrailRouteDef } from "./navigation-trail.model";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Le suivi d'un OF accepte l'id de DEUX façons : en segment de chemin
 * (`/productions/suivi/<id>`, l'entrée depuis la liste) ou en query param
 * (`/productions/suivi/<onglet>?ofId=<id>`, les onglets). Sans cette
 * réconciliation, ouvrir un OF puis changer d'onglet empilerait DEUX maillons.
 * Le segment n'est un id que s'il ressemble à un uuid — sinon c'est un onglet
 * (`ope`, `bilan-matiere`, …).
 */
function suiviOfId(p: TrailParams): string | undefined {
  return UUID_RE.test(p.segment ?? "") ? p.segment : p.ofId;
}

/**
 * REGISTRE DU FIL DE NAVIGATION.
 *
 * Une entrée par route (ou famille de routes). Le matching se fait par PRÉFIXE
 * de segments : `/specification/id/:articleId` couvre aussi
 * `/specification/id/X/Y/nomenclature`. Quand plusieurs motifs matchent, le
 * plus long gagne — donc on peut déclarer le niveau feature ET le niveau
 * élément sans se marcher dessus.
 *
 * Deux leviers :
 * - `silent`  → la route ne produit aucun maillon ;
 * - `key`     → deux URLs de même clé = le MÊME maillon (mis à jour, pas
 *               empilé). C'est ce qui rend muettes les redirections
 *               automatiques (`/specification/id/X` → `/…/X/Y/infos`).
 *
 * Les `resolve` sont chargés en `import()` dynamique : aucune dépendance
 * statique de `shared/` vers les features (pas de cycle d'import, pas
 * d'instanciation des stores au boot). Ils LISENT le store, ils ne chargent
 * jamais de donnée — la page s'en charge, le libellé se met à jour tout seul.
 *
 * Les icônes sont prises dans le jeu déjà utilisé par l'app (police locale).
 */
export const NAV_TRAIL_ROUTES: TrailRouteDef[] = [
  // ─────────────────────────────────────────────────────────────────────
  // Routes muettes — hors shell applicatif ou sans intérêt d'historique
  // ─────────────────────────────────────────────────────────────────────
  { match: "/auth", silent: true },
  { match: "/link", silent: true },
  { match: "/qr", silent: true },
  { match: "/admin", silent: true },
  { match: "/live-production", silent: true },
  { match: "/onboarding", silent: true },

  // ─────────────────────────────────────────────────────────────────────
  // Tableau de bord — maillon comme les autres : AUCUNE route ne remet le
  // fil à zéro, l'effacement est un geste explicite (menu « … »).
  // ─────────────────────────────────────────────────────────────────────
  {
    match: "/dashboard",
    key: () => "dashboard",
    label: "sideNav.menu.dashboard",
    icon: "dashboard",
  },

  // ─────────────────────────────────────────────────────────────────────
  // Données de base
  // ─────────────────────────────────────────────────────────────────────
  {
    match: "/articles",
    key: () => "articles",
    label: "sideNav.menu.articles",
    icon: "inventory_2",
  },
  {
    match: "/articles/inventory",
    key: () => "articles-inventory",
    label: "routing.comptage",
    icon: "fact_check",
  },
  {
    match: "/specification/list",
    key: () => "specifications",
    label: "sideNav.menu.etapes",
    icon: "description",
  },
  {
    match: "/specification/id/:articleId",
    key: (p) => `article:${p.articleId}`,
    label: "routing.article_infos",
    icon: "inventory_2",
    resolve: async (p, injector) => {
      const { ArticlesInfosFacadeService } = await import(
        "../../../features/articles/services/article-infos/article-infos-facade.service"
      );
      const infos = injector
        .get(ArticlesInfosFacadeService)
        .getByArticleId(p.articleId);
      return computed(() => {
        const i = infos();
        if (!i) return null;
        return { label: i.denomination || i.code, sub: i.code };
      });
    },
  },
  {
    match: "/specification/master/:masterId",
    key: (p) => `specif-master:${p.masterId}`,
    label: "sideNav.menu.models",
    icon: "description",
    resolve: async (p, injector) => {
      const { SpecificationFacadeService } = await import(
        "../../../features/specifications/services/specification-facade.service"
      );
      const sem = injector
        .get(SpecificationFacadeService)
        .getById(p.masterId);
      return computed(() => sem()?.denomination);
    },
  },
  {
    match: "/lignes",
    key: () => "lignes",
    label: "sideNav.menu.ligne",
    icon: "view_stream",
  },
  {
    match: "/lignes/id/:id",
    key: (p) => `ligne:${p.id}`,
    label: "routing.lignes_prod",
    icon: "view_stream",
    resolve: async (p, injector) => {
      const { LignesFacadeService } = await import(
        "../../../features/ligne/services/lignes/lignes-facade.service"
      );
      const ligne = injector.get(LignesFacadeService).getById(p.id);
      return computed(() => ligne()?.denomination);
    },
  },
  {
    match: "/parametrage-activite",
    key: () => "parametrage-activite",
    label: "sideNav.menu.activite_param",
    icon: "tune",
  },
  {
    match: "/tiers",
    key: () => "tiers",
    label: "sideNav.menu.tiers",
    icon: "store",
  },
  {
    match: "/tiers/id/:id",
    key: (p) => `tiers:${p.id}`,
    label: "routing.tiers",
    icon: "storefront",
    resolve: async (p, injector) => {
      const { TiersFacadeService } = await import(
        "../../../features/tiers/services/tiers/tiers-facade.service"
      );
      const tiers = injector.get(TiersFacadeService).getById(p.id);
      return computed(() => tiers()?.denomination || tiers()?.code);
    },
  },
  {
    match: "/tiers/groups",
    key: () => "tiers-groups",
    label: "routing.groupes",
    icon: "groups",
  },
  {
    match: "/tiers/groups/:id",
    key: (p) => `tiers-group:${p.id}`,
    label: "routing.groupe",
    icon: "groups",
    resolve: async (p, injector) => {
      const { TierGroupFacadeService } = await import(
        "../../../features/tiers/services/tier-group/tier-group-facade.service"
      );
      const group = injector.get(TierGroupFacadeService).getById(p.id);
      return computed(() => group()?.denomination);
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Tâches journalières
  // ─────────────────────────────────────────────────────────────────────
  {
    match: "/lists",
    key: () => "lists",
    label: "sideNav.menu.todo",
    icon: "checklist",
  },
  {
    match: "/lists/id/:id",
    key: (p) => `list:${p.id}`,
    label: "sideNav.menu.todo",
    icon: "checklist",
    resolve: async (p, injector) => {
      const { ListFacadeService } = await import(
        "../../../features/list/services/store/list-facade.service"
      );
      const list = injector.get(ListFacadeService).getById(p.id);
      return computed(() => list()?.denomination);
    },
  },
  {
    match: "/lists/do/:id",
    key: (p) => `list-do:${p.id}`,
    label: "sideNav.menu.todo",
    icon: "task_alt",
    resolve: async (p, injector) => {
      const { ListFacadeService } = await import(
        "../../../features/list/services/store/list-facade.service"
      );
      const list = injector.get(ListFacadeService).getById(p.id);
      return computed(() => list()?.denomination);
    },
  },
  {
    match: "/info-journalieres",
    key: () => "info-journalieres",
    label: "sideNav.menu.info_journalieres",
    icon: "schedule",
  },

  // ─────────────────────────────────────────────────────────────────────
  // Logistique / inventaire
  // ─────────────────────────────────────────────────────────────────────
  {
    match: "/inventaire",
    key: () => "inventaires",
    label: "sideNav.menu.inventaire",
    icon: "fact_check",
  },
  // Les 3 vues d'un même inventaire (saisie / supervision / bilan) partagent
  // la même clé : on ne veut qu'UN maillon par inventaire.
  ...["id", "supervision", "dashboard"].map(
    (segment): TrailRouteDef => ({
      match: `/inventaire/${segment}/:id`,
      key: (p) => `inventaire:${p.id}`,
      label: "routing.inventaire",
      icon: "fact_check",
      resolve: async (p, injector) => {
        const { InventaireFacadeService } = await import(
          "../../../features/inventaire/services/inventaire-store/inventaire-facade.service"
        );
        const inv = injector.get(InventaireFacadeService).getById(p.id);
        return computed(() => inv()?.denomination);
      },
    }),
  ),
  {
    // Le lot voyage en query param (`/lots/infos?lotId=…`).
    match: "/lots",
    key: (p) => (p.lotId ? `lot:${p.lotId}` : "lots"),
    label: "routing.lot",
    icon: "inventory",
    resolve: async (p, injector) => {
      if (!p.lotId) return computed(() => null);
      const { LotFacadeService } = await import(
        "../../../features/lot/services/store/lot-facade.service"
      );
      const lot = injector.get(LotFacadeService).getById(p.lotId);
      return computed(() => lot()?.numero);
    },
  },
  {
    match: "/lots/all_mouvements",
    key: () => "mouvements",
    label: "sideNav.menu.mouvements",
    icon: "swap_horiz",
  },
  {
    match: "/stock",
    key: () => "stock",
    label: "sideNav.menu.stock",
    icon: "warehouse",
  },
  {
    match: "/stock/id/:articleId",
    key: (p) => `stock-article:${p.articleId}`,
    label: "sideNav.menu.stock",
    icon: "warehouse",
    resolve: async (p, injector) => {
      const { ArticlesInfosFacadeService } = await import(
        "../../../features/articles/services/article-infos/article-infos-facade.service"
      );
      const infos = injector
        .get(ArticlesInfosFacadeService)
        .getByArticleId(p.articleId);
      return computed(() => {
        const i = infos();
        if (!i) return null;
        return { label: i.denomination || i.code, sub: i.code };
      });
    },
  },
  {
    match: "/transferts-entrepot",
    key: () => "transferts-entrepot",
    label: "sideNav.menu.transferts",
    icon: "swap_horiz",
  },
  {
    match: "/transferts-entrepot/details/id/:id",
    key: (p) => `transfert:${p.id}`,
    label: "routing.transfert_entrepot",
    icon: "swap_horiz",
    resolve: async (p, injector) => {
      const { TransfertEntrepotFacadeService } = await import(
        "../../../features/transfert-entrepot/services/store/transfert-entrepot-facade.service"
      );
      const transfert = injector.get(TransfertEntrepotFacadeService).getById(p.id);
      return computed(() => transfert()?.numero);
    },
  },
  {
    match: "/transferts-entrepot/reception/id/:id",
    key: (p) => `transfert-reception:${p.id}`,
    label: "routing.reception_transfert",
    icon: "unarchive",
  },
  {
    match: "/dechargements",
    key: () => "dechargements",
    label: "sideNav.menu.dechargements",
    icon: "unarchive",
  },
  {
    match: "/expeditions",
    key: () => "expeditions",
    label: "sideNav.menu.expeditions",
    icon: "local_shipping",
  },

  // ─────────────────────────────────────────────────────────────────────
  // Production
  // ─────────────────────────────────────────────────────────────────────
  {
    match: "/productions/ofs",
    key: () => "ofs",
    label: "nav_trail.ofs_list",
    icon: "precision_manufacturing",
  },
  {
    match: "/productions/suivi-production-selection",
    key: () => "suivi-production",
    label: "sideNav.menu.suivi_production",
    icon: "precision_manufacturing",
  },
  {
    match: "/productions/suivi-production/:ligneId",
    key: (p) => `suivi-ligne:${p.ligneId}`,
    label: "routing.suivi_fabrication",
    icon: "view_stream",
    resolve: async (p, injector) => {
      const { LignesFacadeService } = await import(
        "../../../features/ligne/services/lignes/lignes-facade.service"
      );
      const ligne = injector.get(LignesFacadeService).getById(p.ligneId);
      return computed(() => ligne()?.denomination);
    },
  },
  // Les deux formes d'URL du suivi d'OF partagent clé et résolution (cf.
  // `suiviOfId`) : `/productions/suivi/<id>` et `/productions/suivi/<onglet>?ofId=`.
  ...["/productions/suivi", "/productions/suivi/:segment"].map(
    (match): TrailRouteDef => ({
      match,
      key: (p) => {
        const id = suiviOfId(p);
        return id ? `of:${id}` : "suivi-of";
      },
      // Repli « OF » puis, dès que le store répond, le numéro — qui porte déjà
      // son préfixe en base (`OF2600164`).
      label: "nav_trail.of",
      icon: "precision_manufacturing",
      resolve: async (p, injector) => {
        const id = suiviOfId(p);
        if (!id) return computed(() => null);
        const { OfFacadeService } = await import(
          "../../../features/production/services/of/of-facade.service"
        );
        const of = injector.get(OfFacadeService).getById(id);
        return computed(() => of()?.numero);
      },
    }),
  ),
  {
    match: "/of-plannif",
    key: () => "of-plannif",
    label: "sideNav.menu.of_plannif",
    icon: "calendar_month",
  },
  {
    match: "/article-planification",
    key: () => "article-planification",
    label: "sideNav.menu.of_plannif_composant",
    icon: "event_note",
  },
  {
    match: "/pdc",
    key: () => "pdc",
    label: "routing.pied_de_chaine",
    icon: "call_split",
  },
  {
    match: "/pdc/reintegration/:ofId",
    key: (p) => `pdc-of:${p.ofId}`,
    label: "nav_trail.of",
    icon: "call_split",
    resolve: async (p, injector) => {
      const { OfFacadeService } = await import(
        "../../../features/production/services/of/of-facade.service"
      );
      const of = injector.get(OfFacadeService).getById(p.ofId);
      return computed(() => of()?.numero);
    },
  },
  {
    match: "/stats",
    key: () => "stats",
    label: "sideNav.menu.stats_prod",
    icon: "bar_chart",
  },
  {
    match: "/dashboard_prod",
    key: () => "dashboard-prod",
    label: "production.dashboard_report",
    icon: "analytics",
  },

  // ─────────────────────────────────────────────────────────────────────
  // Qualité
  // ─────────────────────────────────────────────────────────────────────
  {
    match: "/non-conformites",
    key: () => "non-conformites",
    label: "non_conformite.title",
    icon: "report_problem",
  },
  {
    match: "/tracabilite",
    key: () => "tracabilite",
    label: "sideNav.menu.tracabilite",
    icon: "account_tree",
  },
  {
    match: "/traca",
    key: () => "traca",
    label: "sideNav.menu.tracabilite",
    icon: "account_tree",
  },
  {
    match: "/historique-actions",
    key: () => "historique-actions",
    label: "routing.mouvements",
    icon: "history",
  },

  // ─────────────────────────────────────────────────────────────────────
  // Commandes
  // ─────────────────────────────────────────────────────────────────────
  {
    match: "/commandes",
    key: () => "commandes",
    label: "sideNav.menu.commandes",
    icon: "shopping_bag",
  },
  {
    match: "/commandes/details/id/:id",
    key: (p) => `commande:${p.id}`,
    label: "routing.commande",
    icon: "shopping_bag",
    resolve: async (p, injector) => {
      const { CommandeFacadeService } = await import(
        "../../../features/commande/services/store/commande-facade.service"
      );
      const commande = injector.get(CommandeFacadeService).getById(p.id);
      return computed(() => commande()?.numero);
    },
  },
  {
    match: "/commandes/reception/id/:id",
    key: (p) => `commande-reception:${p.id}`,
    label: "routing.reception",
    icon: "unarchive",
    resolve: async (p, injector) => {
      const { CommandeFacadeService } = await import(
        "../../../features/commande/services/store/commande-facade.service"
      );
      const commande = injector.get(CommandeFacadeService).getById(p.id);
      return computed(() => commande()?.numero);
    },
  },
  {
    match: "/commandeclient",
    key: () => "commandeclient",
    label: "sideNav.menu.commandes_client",
    icon: "shopping_cart",
  },
  {
    match: "/commandeclient/details/id/:id",
    key: (p) => `cc:${p.id}`,
    label: "routing.commande_client",
    icon: "shopping_cart",
    resolve: async (p, injector) => {
      const { CommandeClientFacadeService } = await import(
        "../../../features/commande-client/services/store/commande-client-facade.service"
      );
      const cc = injector.get(CommandeClientFacadeService).getById(p.id);
      return computed(() => cc()?.numero);
    },
  },
  {
    match: "/preparation",
    key: () => "preparation",
    label: "sideNav.menu.preparation",
    icon: "package_2",
  },
  {
    match: "/preparation/produit/:articleId",
    key: (p) => `prepa-article:${p.articleId}`,
    label: "routing.preparation",
    icon: "package_2",
    resolve: async (p, injector) => {
      const { ArticlesInfosFacadeService } = await import(
        "../../../features/articles/services/article-infos/article-infos-facade.service"
      );
      const infos = injector
        .get(ArticlesInfosFacadeService)
        .getByArticleId(p.articleId);
      return computed(() => {
        const i = infos();
        if (!i) return null;
        return { label: i.denomination || i.code, sub: i.code };
      });
    },
  },
  {
    match: "/preparateur",
    key: () => "preparateur",
    label: "routing.preparation",
    icon: "directions_run",
  },
  {
    match: "/preparateur/cc/:id",
    key: (p) => `prep-cc:${p.id}`,
    label: "routing.commande_client",
    icon: "directions_run",
    resolve: async (p, injector) => {
      const { CommandeClientFacadeService } = await import(
        "../../../features/commande-client/services/store/commande-client-facade.service"
      );
      const cc = injector.get(CommandeClientFacadeService).getById(p.id);
      return computed(() => cc()?.numero);
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Administration
  // ─────────────────────────────────────────────────────────────────────
  {
    match: "/facture",
    key: () => "facture",
    label: "sideNav.menu.factures",
    icon: "receipt_long",
  },
  {
    match: "/billing",
    key: () => "billing",
    label: "sideNav.menu.justup_billing",
    icon: "payments",
  },
  {
    match: "/teams",
    key: () => "teams",
    label: "sideNav.menu.equipes",
    icon: "groups",
  },
  {
    match: "/analyse",
    key: () => "analyse",
    label: "sideNav.menu.analyse",
    icon: "analytics",
  },
  {
    match: "/cost-impact",
    key: () => "cost-impact",
    label: "sideNav.menu.cost_impact",
    icon: "calculate",
  },
  {
    match: "/cost-impact-2",
    key: () => "cost-impact-2",
    label: "sideNav.menu.cost_impact_2",
    icon: "calculate",
  },
  {
    match: "/admin-settings",
    key: () => "admin-settings",
    label: "sideNav.menu.admin_param",
    icon: "settings",
  },
  {
    match: "/user-settings",
    key: () => "user-settings",
    label: "sideNav.menu.user_param",
    icon: "settings",
  },
  {
    match: "/group",
    key: () => "group",
    label: "routing.mon_entreprise",
    icon: "business",
  },
  {
    match: "/profile",
    key: () => "profile",
    label: "routing.profil",
    icon: "person",
  },
  {
    match: "/connector",
    key: () => "connector",
    label: "routing.connexion_ecommerce",
    icon: "sync_alt",
  },
  {
    match: "/assistant",
    key: () => "assistant",
    label: "chatbot.title",
    icon: "auto_awesome",
  },
];

/** Icône utilisée quand une route n'est pas (encore) déclarée au registre. */
export const NAV_TRAIL_DEFAULT_ICON = "description";
